/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { Button, Library, LibraryMutations, Title } from '@treams/treams-ui';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';

import EngagementQuestionCard from './EngagementQuestionCard';
import EngagementCategoryTitle from './EngagementCategoryTitle';
import { useTranslation } from 'react-i18next';

const Container = styled(Box)`
	height: 100%;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

export function EngagementLibrary(props: { id: string; inline?: boolean }) {
	const [isArchived, setIsArchived] = useState(false);
	const [version, setVersion] = useState(0);

	useEffect(() => {
		setVersion(version + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isArchived]);

	return (
		<EngagementLibraryInner
			key={version}
			isArchived={isArchived}
			onToggleArchive={setIsArchived}
			{...props}
		/>
	);
}

export function EngagementLibraryInner(props: {
	id: string;
	isArchived: boolean;
	onToggleArchive: (archived: boolean) => void;
	inline?: boolean;
}) {
	const { t } = useTranslation();
	const readOnly = props.id.startsWith('global|');
	const { isArchived } = props;

	const query = graphql.useEngagementCategoryQuery({
		fetchPolicy: 'network-only',
		variables: { id: props.id, isArchived: props.isArchived },
	});

	const questionsQuery = graphql.useEngagementQuestionsByCategoryQuery({
		fetchPolicy: 'network-only',
		variables: { id: props.id, isArchived: props.isArchived },
	});

	const questions =
		!query.data || !questionsQuery.data
			? undefined
			: query.data?.engagementCategory.questions.edges
					.map(
						({ node }) =>
							node as graphql.EngagementQuestion & { category: graphql.EngagementSubcategory },
					)
					.concat(
						props.isArchived
							? (questionsQuery.data.engagementCategory.questions.edges.map(({ node }) => ({
									...node,
									category: {
										id: node.category.isArchived ? node.category.id : '$uncategorized',
										isArchived: node.category.isArchived,
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
									} as any,
							  })) as (graphql.EngagementQuestion & { category: graphql.EngagementSubcategory })[])
							: [],
					);

	const subcategories =
		!query.data || !questionsQuery.data
			? undefined
			: query.data?.engagementCategory.subcategories.edges
					.map(({ node }) => node as graphql.EngagementSubcategory)
					.concat(
						props.isArchived
							? [
									{
										title: [{ languageID: 'en_US', text: t('common.uncategorized') }],
										id: '$uncategorized',
										order: 0,
										isArchived: true,
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										questions: {} as any,
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										category: { id: props.id } as any,
									},
							  ]
							: [],
					);

	const [update] = graphql.useUpdateEngagementCategoryMutation();

	const [createSubcategory] = graphql.useCreateEngagementSubcategoryMutation();
	const [reorderSubcategory] = graphql.useReorderEngagementSubcategoryMutation();
	const [updateSubcategory] = graphql.useUpdateEngagementSubcategoryMutation();
	const [archiveSubcategory] = graphql.useArchiveEngagementSubcategoryMutation();
	const [restoreSubcategory] = graphql.useRestoreEngagementSubcategoryMutation();

	const [reorderQuestion] = graphql.useReorderEngagementQuestionMutation();
	const [archiveQuestion] = graphql.useArchiveEngagementQuestionMutation();
	const [restoreQuestion] = graphql.useRestoreEngagementQuestionMutation();

	const onArchive = async (item: graphql.EngagementQuestion) => {
		if (item.isArchived) {
			await restoreQuestion({ variables: { input: { id: item.id } } });
			return;
		}
		await archiveQuestion({ variables: { input: { id: item.id } } });
	};

	const mutations = useMemo<
		LibraryMutations<graphql.EngagementSubcategory, graphql.EngagementQuestion>
	>(
		() => ({
			createCategory: readOnly
				? undefined
				: async (title) => {
						const response = await createSubcategory({
							variables: { input: { categoryID: props.id, title } },
						});
						return response.data!.createEngagementSubcategory
							.subcategory as graphql.EngagementSubcategory;
				  },
			reorderCategory: readOnly
				? undefined
				: async (id, order) => {
						const response = await reorderSubcategory({
							variables: { input: { id, categoryID: props.id, order } },
						});
						return response.data!.reorderEngagementSubcategory
							.subcategory as graphql.EngagementSubcategory;
				  },
			reorderItem: readOnly
				? undefined
				: async (id, categoryID, order) => {
						const response = await reorderQuestion({
							variables: { input: { id, subcategoryID: categoryID, order } },
						});
						return response.data!.reorderEngagementQuestion
							.question as graphql.EngagementQuestion & {
							category: graphql.EngagementSubcategory;
						};
				  },
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.id, readOnly],
	);

	return (
		<>
			<Box px={4}>
				{query.data ? (
					<EngagementCategoryTitle
						onChange={async (title) => {
							await update({ variables: { input: { id: props.id, title } } });
						}}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						category={query.data.engagementCategory as any}
					/>
				) : (
					<Title.Skeleton />
				)}
			</Box>
			<Library
				inline={props.inline}
				categories={subcategories}
				items={questions}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				mutations={mutations as any}
				archived={props.isArchived}
				onToggleArchive={!readOnly ? props.onToggleArchive : undefined}
				placeholder={
					query.data && !readOnly
						? ({
								title: [],
								type: query.data.engagementTypes.edges[0].node,
						  } as unknown as graphql.EngagementQuestion & {
								category: graphql.EngagementSubcategory;
						  })
						: undefined
				}
				scorer={{
					category() {
						return 1;
					},
					item() {
						return 1;
					},
				}}
				renderer={{
					title(props) {
						return (
							<Library.Category.Title
								isArchived={props.category.isArchived}
								name={props.category.title}
								query={props.query}
								active={props.active}
								loading={props.loading}
								dragging={props.dragging}
								listeners={props.listeners}
								onArchive={
									readOnly || props.category.id === '$uncategorized'
										? undefined
										: async () => {
												await archiveSubcategory({
													variables: { input: { id: props.category.id } },
												});

												props.onForgetCategory(props.category);
										  }
								}
								onRestore={
									readOnly || props.category.id === '$uncategorized'
										? undefined
										: async () => {
												await restoreSubcategory({
													variables: { input: { id: props.category.id } },
												});

												const includedQuestions = questions?.filter(
													(question) => question.category.id === props.category.id,
												);

												for (const question of includedQuestions ?? []) {
													if (question.isArchived) {
														await restoreQuestion({ variables: { input: { id: question.id } } });
													}
												}

												props.onForgetCategory(props.category);
										  }
								}
								onUpdate={
									readOnly
										? undefined
										: async (title) => {
												const response = await updateSubcategory({
													variables: { input: { id: props.category.id, title } },
												});
												const category = response.data!.updateEngagementSubcategory
													.subcategory as graphql.EngagementSubcategory;
												props.onUpdateCategory(category);
										  }
								}
							/>
						);
					},
					card(props) {
						return (
							<EngagementQuestionCard
								{...props}
								types={query.data!.engagementTypes.edges.map(({ node }) => node)}
								readOnly={props.readOnly || readOnly}
								buttons={
									(!isArchived || !props.item.category.isArchived) && (
										<Button
											backgroundColor="white"
											type="destructive"
											small
											title={props.item.isArchived ? t('common.restore') : t('common.archive')}
											ml={2}
											onClick={async () => {
												await onArchive(props.item);
												props.onForget?.(props.item);
											}}
										/>
									)
								}
							/>
						);
					},
				}}
			/>
		</>
	);
}

export default function EngagementLibraryPage(props: { id: string }) {
	return (
		<Container py={4}>
			<EngagementLibrary {...props} />
		</Container>
	);
}
